// A bed choice's "promotion" is not always a discount: a supplement is a
// surcharge the hotel attaches the same way. For those, `totalPriceWithPromo`
// is not what the room is quoted at — the room selection has always shown, and
// booked, `totalPriceWithoutPromo` instead.
//
// The rule used to live only inside the room-selection modal, which rewrites
// the availability it renders. The trip designer suggested a room straight from
// the raw availability, so on a room whose first bed choice carries a
// supplement it quoted an amount the modal then contradicted (KD-3618).
const SUPPLEMENT_PROMOTION_KINDS = ['supplement', 'supplement_percent'];

export const isSupplementPromotion = (bedChoice) =>
  SUPPLEMENT_PROMOTION_KINDS.includes(bedChoice?.promotionInfo?.kind);

// What a bed choice actually costs, whichever way its promotion goes.
export const getBedChoicePrice = (bedChoice) => {
  if (!bedChoice) {
    return 0;
  }

  const price = isSupplementPromotion(bedChoice)
    ? bedChoice.totalPriceWithoutPromo
    : bedChoice.totalPriceWithPromo;

  return price ?? 0;
};

// What a room costs: the bed that was picked, or the one the availability leads
// with when none was.
export const getRoomPrice = (room) =>
  getBedChoicePrice(room?.bed ?? room?.bedChoices?.[0]);

// Sorts rooms cheapest first, on the price actually charged.
export const byRoomPrice = (roomA, roomB) =>
  getRoomPrice(roomA) - getRoomPrice(roomB);

// KD-3732 — the bed configurations of a room that suit `pax` travellers. The
// availability lists, per room, every configuration that suits ANY of the
// requested rooms, so a room of 1 was also offered the extra-bed configurations
// meant for a room of 3. Own inventory states each configuration's capacity;
// third-party configurations carry none and are all kept, as is the whole list
// when none fits.
export const getBedChoicesForPax = (room, pax) => {
  const bedChoices = room?.bedChoices ?? [];
  if (!pax) {
    return bedChoices;
  }

  const fitting = bedChoices.filter(
    ({ minCapacity, maxCapacity }) =>
      pax >= (minCapacity ?? 0) && pax <= (maxCapacity ?? Infinity),
  );

  return fitting.length > 0 ? fitting : bedChoices;
};

// The configuration a room is suggested with: the cheapest of those that suit,
// an extra bed only when no plain configuration does (KD-3758). A room of
// 1 adult + 1 child suits both "1 King bed" and "1 King bed + 1 Extra bed"
// (2-3): it is still offered the extra bed, but suggested the plain bed.
export const getBedChoiceForPax = (room, pax) => {
  const bedChoices = getBedChoicesForPax(room, pax);
  const withoutExtraBed = bedChoices.filter(({ hasExtraBed }) => !hasExtraBed);

  return (withoutExtraBed.length > 0 ? withoutExtraBed : bedChoices).reduce(
    (cheapest, bedChoice) =>
      !cheapest || getBedChoicePrice(bedChoice) < getBedChoicePrice(cheapest)
        ? bedChoice
        : cheapest,
    undefined,
  );
};
